'use client';

import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'next-i18next';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { Modal, Spinner, bjToast } from '@components/common';
import { InterviewAnswerBar } from './InterviewAnswerBar';

export interface InterviewQuestionnaireModalProps {
  getQuestions: () => Promise<string[]>;
  locale: Locale;
  /** Opening line the voice agent speaks first. */
  firstMessage?: string;
  /** Invoked when the flow finishes / the modal closes (e.g. to refresh the answered gate). */
  onFinished?: () => void;
}

/**
 * Question-by-question interview flow used by the banner. Fetches the questions on open and shows
 * one at a time with the shared `InterviewAnswerBar` (text / audio / video). Advancing (answer or
 * skip) moves to the next question; finishing (or closing) calls `onFinished`.
 */
export const InterviewQuestionnaireModal = NiceModal.create<InterviewQuestionnaireModalProps>(
  ({
    getQuestions, locale, firstMessage, onFinished,
  }) => {
    const { t } = useTranslation('common');
    const modal = useModal();
    const [questions, setQuestions] = useState<string[] | null>(null);
    const [index, setIndex] = useState(0);
    const startedRef = useRef(false);

    const closeModal = useCallback(() => {
      onFinished?.();
      void modal.hide();
      modal.remove();
    }, [modal, onFinished]);

    useEffect(() => {
      if (startedRef.current) return;
      startedRef.current = true;

      void (async () => {
        try {
          const resolved = (await getQuestions()).map((q) => q.trim()).filter(Boolean);
          if (!resolved.length) {
            bjToast.error(t('audio-interview.job-landing.error'));
            closeModal();
            return;
          }
          setQuestions(resolved);
        } catch {
          bjToast.error(t('audio-interview.error.connecting'));
          closeModal();
        }
      })();
    }, [getQuestions, closeModal, t]);

    const advance = useCallback(() => {
      setIndex((i) => {
        const next = i + 1;
        if (questions && next >= questions.length) {
          closeModal();
          return i;
        }
        return next;
      });
    }, [questions, closeModal]);

    const current = questions?.[index];

    return (
      <Modal
        isOpen={modal.visible}
        onClose={closeModal}
        size="lg"
        className="!rounded-3xl"
        body={(!questions || !current) ? (
          <div className="flex min-h-64 items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <div className="flex flex-col gap-6 p-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-ink-medium">{`${index + 1} / ${questions.length}`}</span>
              <button
                type="button"
                onClick={closeModal}
                aria-label={t('global.btn.close')}
                className="flex size-9 items-center justify-center rounded-full text-ink-medium transition-colors hover:bg-surface-100"
              >
                <XMarkIcon className="size-5" />
              </button>
            </div>

            <h3 className="text-lg font-semibold leading-snug">{current}</h3>

            <InterviewAnswerBar
              key={current}
              question={current}
              locale={locale}
              firstMessage={firstMessage}
              onAnswered={advance}
            />

            <button
              type="button"
              onClick={advance}
              className="self-end text-sm text-ink-medium underline"
            >
              {t('global.action.skip', 'Skip')}
            </button>
          </div>
        )}
      />
    );
  },
);
